/**
 * System Health Dashboard — pure Sentry-rate aggregation helpers (Day 99b.1).
 *
 * Isolated from SystemHealthDashboard.tsx so the bucketing logic is unit-testable
 * without pulling recharts / @sentry/react / the supabase client. `now` is
 * injectable on every helper for deterministic tests.
 */

export interface DayBucket {
  /** stable per-day key (local calendar day) — for de-dup/indexing */
  key: string;
  /** short axis label, e.g. "6/4" */
  label: string;
  count: number;
}

const DAY_MS = 86_400_000;

/** ISO timestamp `ms` milliseconds before `now` — the PostgREST `.gte` cutoff. */
export function isoCutoff(ms: number, now: number = Date.now()): string {
  return new Date(now - ms).toISOString();
}

function localDayKey(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Bucket event timestamps into the last `days` local-calendar days (inclusive of
 * today), oldest→newest. Timestamps outside the window — or unparseable — are
 * ignored. Always returns exactly `days` buckets (zero-count days included), so a
 * quiet week renders as a flat baseline rather than a gap.
 */
export function bucketEventsByDay(
  timestamps: Array<string | null | undefined>,
  days: number,
  now: number = Date.now()
): DayBucket[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayStart = today.getTime();

  const buckets: DayBucket[] = [];
  const indexByKey = new Map<string, number>();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(todayStart - i * DAY_MS);
    const key = localDayKey(d.getTime());
    indexByKey.set(key, buckets.length);
    buckets.push({ key, label: `${d.getMonth() + 1}/${d.getDate()}`, count: 0 });
  }

  for (const ts of timestamps) {
    if (!ts) continue;
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) continue;
    const idx = indexByKey.get(localDayKey(t));
    if (idx !== undefined) buckets[idx].count += 1;
  }
  return buckets;
}

/** Total across all buckets — the "any errors this window?" headline. */
export function totalCount(buckets: DayBucket[]): number {
  return buckets.reduce((sum, b) => sum + b.count, 0);
}
